import React, {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
  useContext,
} from 'react';
import debounce from 'lodash.debounce';
import { WalletContext } from './WalletContext';
import { toast } from 'react-toastify';
import { ethers } from 'ethers';
import CashStakingABI from '../abi/CashStaking.json';
import CashNFTABI from '../abi/CashNFT.json';
import CashTokenABI from '../abi/CashisDead.json';
import {
  CASHSTAKING_ADDRESS,
  CASHISDEAD_ADDRESS as CASH_ADDRESS,
  NETWORK_CONFIG,
} from '../contracts/constants';
import dayjs from 'dayjs';

export const StakingContext = createContext();

export const StakingProvider = ({ children }) => {
  const { account, provider, chainId } = useContext(WalletContext);

  const [stakingPositions, setStakingPositions] = useState([]);
  const [stakingPeriod, setStakingPeriod] = useState(0);
  const [isTransactionPending, setIsTransactionPending] = useState(false);
  const [loadingStake, setLoadingStake] = useState(false);
  const [loadingUnstake, setLoadingUnstake] = useState(false);
  const [contractBalance, setContractBalance] = useState('0');
  const [totalStaked, setTotalStaked] = useState('0');
  const [cashBalance, setCashBalance] = useState('0');
  const [minimumStakeAmount, setMinimumStakeAmount] = useState('0');
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const stakingContractRef = useRef(null);
  const nftContractRef = useRef(null);
  const contractsInitializedRef = useRef(false);
  const lastInitAttemptRef = useRef(0);
  const hasShownNetworkToastRef = useRef(false);

  const setTransactionPendingDebounced = useMemo(
    () => debounce((value) => setIsTransactionPending(value), 500),
    []
  );

  const retryCall = useCallback(async (fn, maxAttempts = 3) => {
    for (let i = 0; i < maxAttempts; i++) {
      try {
        return await fn();
      } catch (error) {
        const errMsg = (error.message || '').toLowerCase();
        const dataMsg = (error?.data?.message || '').toLowerCase();
        const errorStr = JSON.stringify(error).toLowerCase();
        
        // Check for various RPC/ rate limit errors
        const isRetryableError = 
          error.code === 'SERVER_ERROR' ||
          error.code === 'TIMEOUT' ||
          error.code === 'NETWORK_ERROR' ||
          error.code === -32603 ||
          error.code === -32005 ||
          error.code === -32002 ||
          error?.data?.code === -32094 ||
          error?.data?.code === -32005 ||
          errMsg.includes("limit exceeded") ||
          errMsg.includes("blockchain height exceeded") ||
          errMsg.includes("bad result from backend") ||
          errMsg.includes("rate limit") ||
          errMsg.includes("rate exceeded") ||
          errMsg.includes("too many requests") ||
          errMsg.includes("call exception") ||
          errMsg.includes("missing revert data") ||
          errMsg.includes("block is out of range") ||
          dataMsg.includes("blockchain height exceeded") ||
          errorStr.includes("rate limit") ||
          errorStr.includes("bad result");
          
        if (isRetryableError && i < maxAttempts - 1) {
          const delay = Math.pow(2, i) * 1500; // Increased delay
          await new Promise((resolve) => setTimeout(resolve, delay));
          continue;
        }
        throw error;
      }
    }
    return null;
  }, []);

  const fetchFunctions = useMemo(() => ({
    fetchStakingPositions: async (isRefresh = false) => {
      const stakingContract = stakingContractRef.current;
      if (!stakingContract || !account || !contractsInitializedRef.current) return;
      try {
        const network = await provider.getNetwork();
        if (network.chainId !== NETWORK_CONFIG.chainId) {
          throw new Error(`Connected to the wrong network. Please switch to ${NETWORK_CONFIG.name}.`);
        }
        const code = await provider.getCode(stakingContract.address);
        if (code === '0x') {
          throw new Error(`Staking contract does not exist on ${NETWORK_CONFIG.name}.`);
        }
        if (!stakingContract.getPositions) {
          throw new Error("'getPositions' method does not exist on the staking contract.");
        }
        const positions = await retryCall(() => stakingContract.getPositions(account));
        const formattedPositions = await Promise.all(
          positions.map(async (pos) => {
            try {
              const rewardPercentage = await retryCall(() =>
                stakingContract.rewardPercentages(pos.stakingPeriod)
              );
              return {
                id: pos.positionId.toString(),
                positionId: pos.positionId.toString(),
                amount: ethers.utils.formatUnits(pos.amount, 18),
                rewardPercentage: rewardPercentage.toString(),
                stakingStartTimestamp: pos.stakingStartTimestamp.toString(),
                stakingEndTimestamp: pos.stakingEndTimestamp.toString(),
                rewardsClaimed: pos.rewardsClaimed,
                stakingPeriod: Number(pos.stakingPeriod),
              };
            } catch {
              return {
                id: pos.positionId.toString(),
                positionId: pos.positionId.toString(),
                amount: ethers.utils.formatUnits(pos.amount, 18),
                rewardPercentage: '0',
                stakingStartTimestamp: pos.stakingStartTimestamp.toString(),
                stakingEndTimestamp: pos.stakingEndTimestamp.toString(),
                rewardsClaimed: pos.rewardsClaimed,
                stakingPeriod: Number(pos.stakingPeriod),
              };
            }
          })
        );
        setStakingPositions(formattedPositions);
      } catch (error) {
        // Silent fail on refresh - no need for toast
        if (error.message.includes('wrong network')) {
          // Only show toast if not already shown recently
          if (!hasShownNetworkToastRef.current) {
            hasShownNetworkToastRef.current = true;
            toast.error(error.message);
            // Reset after 10 seconds
            setTimeout(() => { hasShownNetworkToastRef.current = false; }, 10000);
          }
        }
      }
    },

    fetchContractBalance: async () => {
      const stakingContract = stakingContractRef.current;
      if (!stakingContract || !contractsInitializedRef.current) return;
      try {
        const cashTokenContract = new ethers.Contract(CASH_ADDRESS, CashTokenABI, provider);
        const balance = await retryCall(() =>
          cashTokenContract.balanceOf(stakingContract.address)
        );
        setContractBalance(ethers.utils.formatUnits(balance, 18));
        const totalStakedVal = await retryCall(() => stakingContract.totalStaked());
        setTotalStaked(ethers.utils.formatUnits(totalStakedVal, 18));
      } catch {}
    },

    fetchUserCashBalance: async () => {
      if (!account || !provider) return;
      try {
        const cashTokenContract = new ethers.Contract(CASH_ADDRESS, CashTokenABI, provider);
        const balance = await retryCall(() => cashTokenContract.balanceOf(account));
        setCashBalance(ethers.utils.formatUnits(balance, 18));
      } catch {}
    },
  }), [account, provider, retryCall]);

  const initializeStakingContract = useCallback(async () => {
    if (!provider || !account || !chainId || isInitializing) return;
    
    // Early check - don't even try if on wrong network
    if (chainId !== NETWORK_CONFIG.chainId) {
      setIsInitialized(false);
      contractsInitializedRef.current = false;
      return;
    }
    
    // Throttle: prevent repeated init attempts within 15 seconds (longer to avoid rate limit spam)
    const now = Date.now();
    if (now - lastInitAttemptRef.current < 15000) {
      return;
    }
    lastInitAttemptRef.current = now;
    
    try {
      setIsInitializing(true);
      const network = await provider.getNetwork();
      if (network.chainId !== NETWORK_CONFIG.chainId) {
        // Silently return without throwing - wrong network is expected sometimes
        setIsInitialized(false);
        contractsInitializedRef.current = false;
        return;
      }
      const stakingContract = new ethers.Contract(
        CASHSTAKING_ADDRESS,
        CashStakingABI,
        provider.getSigner()
      );
      const code = await retryCall(() => provider.getCode(stakingContract.address));
      if (code === '0x') {
        // RPC may return 0x during rate-limit/overload — treat as transient, back off
        lastInitAttemptRef.current = Date.now() + 30000;
        setIsInitialized(false);
        contractsInitializedRef.current = false;
        setIsInitializing(false);
        return;
      }
      if (
        typeof stakingContract.getPositions !== 'function' ||
        typeof stakingContract.stakingToken !== 'function' ||
        typeof stakingContract.rewardPercentages !== 'function' ||
        typeof stakingContract.stake !== 'function' ||
        typeof stakingContract.totalStaked !== 'function' ||
        typeof stakingContract.minimumStakeAmount !== 'function' ||
        typeof stakingContract.nftContract !== 'function'
      ) {
        throw new Error('Staking contract does not have required functions.');
      }
      const nftContractAddress = await retryCall(() => stakingContract.nftContract());
      if (nftContractAddress === ethers.constants.AddressZero) {
        throw new Error(`NFT contract address not set in staking contract on ${NETWORK_CONFIG.name}.`);
      }
      const nftContract = new ethers.Contract(nftContractAddress, CashNFTABI, provider);
      nftContractRef.current = nftContract;
      stakingContractRef.current = stakingContract;
      const initialTotalStaked = await retryCall(() => stakingContract.totalStaked());
      setTotalStaked(ethers.utils.formatUnits(initialTotalStaked, 18));
      const minStake = await retryCall(() => stakingContract.minimumStakeAmount());
      setMinimumStakeAmount(ethers.utils.formatUnits(minStake, 18));
      contractsInitializedRef.current = true;
      setIsInitialized(true);
      await fetchFunctions.fetchStakingPositions();
      await fetchFunctions.fetchContractBalance();
      await fetchFunctions.fetchUserCashBalance();
    } catch (error) {
      // Only log if it's not a common RPC error
      const errMsg = (error.message || '').toLowerCase();
      const errStr = JSON.stringify(error).toLowerCase();
      const isCommonError = 
        error.code === 'SERVER_ERROR' ||
        error.code === 'TIMEOUT' ||
        error.code === 'NETWORK_ERROR' ||
        error.code === -32005 ||
        error.code === -32603 ||
        error.code === -32002 ||
        errMsg.includes('rate limit') ||
        errMsg.includes('bad result') ||
        errMsg.includes('limit exceeded') ||
        errMsg.includes('blockchain height') ||
        errMsg.includes('switch to the correct network') ||
        errStr.includes('limit exceeded') ||
        errStr.includes('rate limit');
        
      if (!isCommonError) {
        console.warn(`Staking contract initialization error on ${NETWORK_CONFIG.name}:`, error);
      } else {
        // Extend throttle on rate limit errors to avoid spamming RPC
        lastInitAttemptRef.current = Date.now() + 30000; // block further attempts for 30s
      }
      setIsInitialized(false);
      contractsInitializedRef.current = false;
      stakingContractRef.current = null;
      nftContractRef.current = null;
    } finally {
      setIsInitializing(false);
    }
  }, [provider, account, chainId, fetchFunctions, isInitializing, retryCall]);

  useEffect(() => {
    initializeStakingContract();
  }, [initializeStakingContract]);

  useEffect(() => {
    if (!provider || !account) {
      setStakingPositions([]);
      setTotalStaked('0');
      setContractBalance('0');
      setCashBalance('0');
      stakingContractRef.current = null;
      nftContractRef.current = null;
      contractsInitializedRef.current = false;
      setIsInitialized(false);
      setStakingPeriod(0);
      setMinimumStakeAmount('0');
    }
  }, [provider, account]);

  const fetchUserBalance = useCallback(async () => {
    if (account && contractsInitializedRef.current) {
      await fetchFunctions.fetchUserCashBalance();
    } else {
      setCashBalance('0');
    }
  }, [account, fetchFunctions]);

  useEffect(() => {
    fetchUserBalance();
  }, [fetchUserBalance]);

  const fetchInitialData = useCallback(async () => {
    if (!account || !contractsInitializedRef.current) return;
    try {
      await Promise.all([
        fetchFunctions.fetchStakingPositions(),
        fetchFunctions.fetchContractBalance(),
        fetchFunctions.fetchUserCashBalance(),
      ]);
    } catch {}
  }, [account, fetchFunctions]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const calculateMaxStakeAmount = useCallback(async (rawBalance) => {
    if (!rawBalance) return '0';
    const balanceBN = ethers.BigNumber.isBigNumber(rawBalance)
      ? rawBalance
      : ethers.utils.parseUnits(rawBalance.toString(), 18);
    const reserveAmount = ethers.utils.parseUnits('1', 18);
    if (balanceBN.lte(reserveAmount)) {
      return '0';
    }
    const maxStakeAmount = balanceBN.sub(reserveAmount);
    return ethers.utils.formatUnits(maxStakeAmount, 18);
  }, []);

  const stakeTokens = useCallback(
    async (amount, period) => {
      if (!stakingContractRef.current || !contractsInitializedRef.current) {
        toast.error(`Staking contract not initialized on ${NETWORK_CONFIG.name}.`);
        throw new Error('Staking contract not initialized.');
      }
      try {
        setTransactionPendingDebounced(true);
        setLoadingStake(true);
        const stakingContract = stakingContractRef.current;
        const signer = provider.getSigner();
        const nftContractAddress = await retryCall(() => stakingContract.nftContract());
        if (nftContractAddress === ethers.constants.AddressZero) {
          throw new Error(`NFT contract not set in staking contract on ${NETWORK_CONFIG.name}.`);
        }
        if (period === undefined || period === null) {
          throw new Error('Invalid staking period');
        }
        if (!amount || isNaN(amount) || parseFloat(amount) <= 0) {
          throw new Error('Invalid stake amount');
        }
        const amountInWei = ethers.utils.parseUnits(amount.toString(), 18);
        const cashTokenAddress = await retryCall(() => stakingContract.stakingToken());
        const cashTokenContract = new ethers.Contract(
          cashTokenAddress,
          ["function balanceOf(address) view returns (uint256)",
           "function allowance(address,address) view returns (uint256)",
           "function approve(address,uint256) returns (bool)"],
          signer
        );
        const userBalance = await retryCall(() => cashTokenContract.balanceOf(account));
        const maxStakeAmountWei = ethers.utils.parseUnits(
          await calculateMaxStakeAmount(userBalance),
          18
        );
        if (amountInWei.gt(maxStakeAmountWei)) {
          throw new Error(
            `Cannot stake more than ${await calculateMaxStakeAmount(userBalance)} CASH`
          );
        }
        const minStakeInWei = ethers.utils.parseUnits(minimumStakeAmount, 18);
        if (amountInWei.lt(minStakeInWei)) {
          toast.error(`Minimum stake amount is ${minimumStakeAmount} $CASH.`);
          throw new Error('Amount below minimum stake.');
        }
        const isPaused = await retryCall(() => stakingContract.paused());
        if (isPaused) {
          throw new Error('Staking is currently paused');
        }
        const duration = await retryCall(() => stakingContract.stakingDurations(period));
        if (duration.eq(0)) {
          throw new Error('Invalid staking period');
        }
        if (userBalance.lt(amountInWei)) {
          throw new Error('Insufficient $CASH balance');
        }
        const allowance = await retryCall(() => cashTokenContract.allowance(account, stakingContract.address));
        if (allowance.lt(amountInWei)) {
          try {
            const txApprove = await cashTokenContract.approve(
              stakingContract.address,
              ethers.constants.MaxUint256
            );
            toast.info('Approving $CASH tokens...');
            await txApprove.wait();
          } catch (error) {
            if (
              error.code === 4001 ||
              error.code === 'ACTION_REJECTED' ||
              (error.message || '').toLowerCase().includes('user rejected') ||
              (error.message || '').toLowerCase().includes('user denied')
            ) {
              return;
            }
            throw error;
          }
        }
        const estimatedGas = await stakingContract.estimateGas.stake(amountInWei, period);
        const txStake = await stakingContract.stake(amountInWei, period, {
          gasLimit: Math.floor(estimatedGas.toNumber() * 1.2),
        });
        toast.info('Staking tokens...');
        const receipt = await txStake.wait();
        if (receipt.status === 0) {
          toast.error('Staking transaction failed.');
          return;
        }
        toast.success('Successfully staked your tokens!');
        await fetchFunctions.fetchStakingPositions();
        await fetchFunctions.fetchContractBalance();
        await fetchFunctions.fetchUserCashBalance();
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === 'ACTION_REJECTED' ||
          (error.message || '').toLowerCase().includes('user rejected') ||
          (error.message || '').toLowerCase().includes('user denied')
        ) {
          return;
        }
        let errorMessage = 'Staking failed.';
        if (error.reason) {
          errorMessage = error.reason;
        } else if (error.message) {
          errorMessage = error.message;
        }
        toast.error(errorMessage);
        throw error;
      } finally {
        setTransactionPendingDebounced(false);
        setLoadingStake(false);
      }
    },
    [
      account,
      minimumStakeAmount,
      provider,
      setTransactionPendingDebounced,
      fetchFunctions,
      calculateMaxStakeAmount,
      retryCall,
    ]
  );

  const unstakeTokens = useCallback(
    async (positionId) => {
      if (!stakingContractRef.current || !contractsInitializedRef.current) {
        toast.error(`Staking contract not initialized on ${NETWORK_CONFIG.name}.`);
        throw new Error('Staking contract not initialized.');
      }
      try {
        setTransactionPendingDebounced(true);
        setLoadingUnstake(true);
        const tx = await stakingContractRef.current
          .connect(provider.getSigner())
          .unstake(positionId, { gasLimit: 200000 });
        toast.info('Unstaking transaction submitted...');
        await tx.wait();
        await fetchFunctions.fetchStakingPositions();
        await fetchFunctions.fetchContractBalance();
        await fetchFunctions.fetchUserCashBalance();
        toast.success('Tokens unstaked successfully.');
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === 'ACTION_REJECTED' ||
          (error.message || '').toLowerCase().includes('user rejected') ||
          (error.message || '').toLowerCase().includes('user denied')
        ) {
          return;
        }
        if (error.code === 4001) {
          toast.error('Transaction rejected by user.');
        } else if (error.message.includes('revert')) {
          const revertReason = error.data?.message || error.reason || 'Transaction reverted';
          toast.error(`Unstaking failed: ${revertReason}`);
        } else {
          toast.error('Unstaking tokens failed.');
        }
        throw error;
      } finally {
        setTransactionPendingDebounced(false);
        setLoadingUnstake(false);
      }
    },
    [provider, fetchFunctions, setTransactionPendingDebounced]
  );

  const claimReward = useCallback(
    async (positionId) => {
      if (!stakingContractRef.current || !contractsInitializedRef.current) {
        toast.error(`Staking contract not initialized on ${NETWORK_CONFIG.name}.`);
        throw new Error('Staking contract not initialized.');
      }
      try {
        setTransactionPendingDebounced(true);
        const tx = await stakingContractRef.current
          .connect(provider.getSigner())
          .claimRewards(positionId, { gasLimit: 150000 });
        toast.info('Claiming rewards...');
        await tx.wait();
        await fetchFunctions.fetchStakingPositions();
        await fetchFunctions.fetchContractBalance();
        await fetchFunctions.fetchUserCashBalance();
        toast.success('Reward claimed successfully.');
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === 'ACTION_REJECTED' ||
          (error.message || '').toLowerCase().includes('user rejected') ||
          (error.message || '').toLowerCase().includes('user denied')
        ) {
          return;
        }
        if (error.code === 4001) {
          toast.error('Transaction rejected by user.');
        } else if (error.message.includes('revert')) {
          const revertReason = error.data?.message || error.reason || 'Transaction reverted';
          toast.error(`Failed to claim reward: ${revertReason}`);
        } else {
          toast.error('Failed to claim reward.');
        }
        throw error;
      } finally {
        setTransactionPendingDebounced(false);
      }
    },
    [provider, fetchFunctions, setTransactionPendingDebounced]
  );

  const restakeTokens = useCallback(
    async (positionId, newPeriod) => {
      if (!stakingContractRef.current || !contractsInitializedRef.current) {
        toast.error(`Staking contract not initialized on ${NETWORK_CONFIG.name}.`);
        throw new Error('Staking contract not initialized.');
      }
      try {
        setTransactionPendingDebounced(true);
        setLoadingStake(true);
        const stakingContract = stakingContractRef.current;
        const signer = provider.getSigner();
        const position = stakingPositions.find(
          (pos) => pos.stakingPeriod === newPeriod && pos.positionId === positionId.toString()
        );
        if (!position) {
          toast.error('Staking position not found.');
          throw new Error('Staking position not found.');
        }
        const amountInWei = ethers.utils.parseUnits(position.amount, 18);
        if (!position.rewardsClaimed && dayjs().unix() > parseInt(position.stakingEndTimestamp)) {
          toast.info('Claiming rewards...');
          const claimTx = await stakingContract.connect(signer).claimRewards(positionId, {
            gasLimit: 150000,
          });
          await claimTx.wait();
          toast.success('Rewards claimed.');
        }
        toast.info('Unstaking current position...');
        const unstakeTx = await stakingContract.connect(signer).unstake(positionId, {
          gasLimit: 200000,
        });
        await unstakeTx.wait();
        toast.success('Unstaked successfully.');
        toast.info('Restaking tokens...');
        const stakeTx = await stakingContract.connect(signer).stake(amountInWei, newPeriod, {
          gasLimit: 200000,
        });
        await stakeTx.wait();
        toast.success('Tokens restaked successfully.');
        await fetchFunctions.fetchStakingPositions();
        await fetchFunctions.fetchContractBalance();
        await fetchFunctions.fetchUserCashBalance();
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === 'ACTION_REJECTED' ||
          (error.message || '').toLowerCase().includes('user rejected') ||
          (error.message || '').toLowerCase().includes('user denied')
        ) {
          return;
        }
        if (error.code === 4001) {
          toast.error('Transaction rejected by user.');
        } else if (error.message.includes('revert')) {
          const revertReason = error.data?.message || error.reason || 'Transaction reverted';
          toast.error(`Restaking failed: ${revertReason}`);
        } else {
          toast.error('Restaking tokens failed.');
        }
        throw error;
      } finally {
        setTransactionPendingDebounced(false);
        setLoadingStake(false);
      }
    },
    [stakingPositions, provider, fetchFunctions, setTransactionPendingDebounced]
  );

  const emergencyUnstake = useCallback(
    async (positionId) => {
      if (!stakingContractRef.current || !contractsInitializedRef.current) {
        toast.error(`Staking contract not initialized on ${NETWORK_CONFIG.name}.`);
        throw new Error('Staking contract not initialized.');
      }
      try {
        setTransactionPendingDebounced(true);
        setLoadingUnstake(true);
        const signer = provider.getSigner();
        const tx = await stakingContractRef.current.connect(signer).emergencyUnstake(positionId, {
          gasLimit: 200000,
        });
        toast.info('Emergency unstaking transaction submitted...');
        await tx.wait();
        await fetchFunctions.fetchStakingPositions();
        await fetchFunctions.fetchContractBalance();
        await fetchFunctions.fetchUserCashBalance();
        toast.success('Emergency unstake successful.');
      } catch (error) {
        if (
          error.code === 4001 ||
          error.code === 'ACTION_REJECTED' ||
          (error.message || '').toLowerCase().includes('user rejected') ||
          (error.message || '').toLowerCase().includes('user denied')
        ) {
          return;
        }
        if (error.code === 4001) {
          toast.error('Transaction rejected by user.');
        } else if (error.message.includes('revert')) {
          const revertReason = error.data?.message || error.reason || 'Transaction reverted';
          toast.error(`Emergency unstake failed: ${revertReason}`);
        } else {
          toast.error('Emergency unstake failed.');
        }
        throw error;
      } finally {
        setTransactionPendingDebounced(false);
        setLoadingUnstake(false);
      }
    },
    [provider, fetchFunctions, setTransactionPendingDebounced]
  );

  const setupEventListeners = useCallback(() => {
    if (!stakingContractRef.current) return;
    const handleStake = () => {
      fetchFunctions.fetchStakingPositions();
      fetchFunctions.fetchContractBalance();
      fetchFunctions.fetchUserCashBalance();
    };
    const handleUnstake = () => {
      fetchFunctions.fetchStakingPositions();
      fetchFunctions.fetchContractBalance();
      fetchFunctions.fetchUserCashBalance();
    };
    const handleRewardClaimed = () => {
      fetchFunctions.fetchStakingPositions();
      fetchFunctions.fetchContractBalance();
      fetchFunctions.fetchUserCashBalance();
    };
    stakingContractRef.current.on('Staked', handleStake);
    stakingContractRef.current.on('Unstaked', handleUnstake);
    stakingContractRef.current.on('RewardClaimed', handleRewardClaimed);
    return () => {
      if (stakingContractRef.current) {
        stakingContractRef.current.off('Staked', handleStake);
        stakingContractRef.current.off('Unstaked', handleUnstake);
        stakingContractRef.current.off('RewardClaimed', handleRewardClaimed);
      }
    };
  }, [fetchFunctions]);

  useEffect(() => {
    const cleanup = setupEventListeners();
    return () => {
      if (cleanup) cleanup();
    };
  }, [setupEventListeners]);

  useEffect(() => {
    const fetchData = async () => {
      if (account && contractsInitializedRef.current) {
        await Promise.all([
          fetchFunctions.fetchStakingPositions(),
          fetchFunctions.fetchContractBalance(),
          fetchFunctions.fetchUserCashBalance(),
        ]);
      } else {
        setStakingPositions([]);
        setStakingPeriod(0);
        setLoadingStake(false);
        setLoadingUnstake(false);
        setContractBalance('0');
        setTotalStaked('0');
        setCashBalance('0');
        setIsTransactionPending(false);
      }
    };
    fetchData();
  }, [account, fetchFunctions]);

  // Handle wrong-network state (init on correct network is handled by the other useEffect above)
  useEffect(() => {
    if (chainId && chainId !== NETWORK_CONFIG.chainId) {
      if (!hasShownNetworkToastRef.current) {
        hasShownNetworkToastRef.current = true;
        toast.error(`Please connect to ${NETWORK_CONFIG.name}.`);
      }
      setStakingPositions([]);
      setTotalStaked('0');
      setContractBalance('0');
      setCashBalance('0');
      stakingContractRef.current = null;
      nftContractRef.current = null;
      contractsInitializedRef.current = false;
      setIsInitialized(false);
      setStakingPeriod(0);
      setMinimumStakeAmount('0');
    } else if (chainId === NETWORK_CONFIG.chainId) {
      // Reset toast flag when on correct network
      hasShownNetworkToastRef.current = false;
    }
  }, [chainId]);

  const contextValue = useMemo(
    () => ({
      stakingContract: stakingContractRef.current,
      nftContract: nftContractRef.current,
      stakingPositions,
      stakingPeriod,
      setStakingPeriod,
      isTransactionPending,
      loadingStake,
      loadingUnstake,
      contractBalance,
      totalStaked,
      cashBalance,
      minimumStakeAmount,
      isInitialized,
      isInitializing: isInitializing || loadingStake || loadingUnstake,
      stakeTokens,
      unstakeTokens,
      claimReward,
      restakeTokens,
      emergencyUnstake,
      fetchStakingPositions: fetchFunctions.fetchStakingPositions,
    }),
    [
      stakingPositions,
      stakingPeriod,
      setStakingPeriod,
      isTransactionPending,
      loadingStake,
      loadingUnstake,
      contractBalance,
      totalStaked,
      cashBalance,
      minimumStakeAmount,
      isInitialized,
      isInitializing,
      stakeTokens,
      unstakeTokens,
      claimReward,
      restakeTokens,
      emergencyUnstake,
      fetchFunctions,
    ]
  );

  return <StakingContext.Provider value={contextValue}>{children}</StakingContext.Provider>;
};

export default StakingProvider;